import React, { useState, useEffect } from 'react'
import { BarChart, TrendingUp, Users, Activity, Clock, Award, Calendar, RefreshCw, Download, Filter } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns'

interface Stats {
  users: {
    total: number
    active_today: number
    active_week: number
    active_month: number
    new_this_month: number
  }
  chores: {
    total_assigned: number
    completed: number
    pending: number
    overdue: number
    completion_rate: number
  }
  points: {
    total_earned: number
    total_redeemed: number
    average_per_user: number
    top_earner: {
      name: string
      points: number
    }
  }
  activity: {
    logins_today: number
    logins_week: number
    avg_session_duration: string
    peak_hour: string
  }
  trends: {
    daily_completions: Array<{ date: string; count: number }>
    weekly_points: Array<{ week: string; points: number }>
    user_growth: Array<{ month: string; users: number }>
  }
}

export const UsageStatistics: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month' | 'year'>('week')
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    loadStats()
  }, [timeRange])

  const loadStats = async () => {
    if (!loading) setRefreshing(true)

    try {
      // Get multiple data sources
      const [systemStats, weeklyReport] = await Promise.all([
        apiClient.get('/api/system/stats'),
        apiClient.get('/api/reports/weekly-summary')
      ])

      // Transform the data
      const stats: Stats = {
        users: {
          total: systemStats.users?.total_users || 0,
          active_today: systemStats.users?.active_today || 0,
          active_week: systemStats.users?.active_week || 0,
          active_month: systemStats.users?.active_month || 0,
          new_this_month: systemStats.users?.new_this_month || 0
        },
        chores: {
          total_assigned: systemStats.assignments?.total || weeklyReport.total_assigned || 0,
          completed: systemStats.assignments?.completed || weeklyReport.total_completed || 0,
          pending: systemStats.assignments?.pending || weeklyReport.pending || 0,
          overdue: systemStats.assignments?.overdue || 0,
          completion_rate: weeklyReport.completion_rate ||
            (systemStats.assignments?.completed && systemStats.assignments?.total
              ? Math.round((systemStats.assignments.completed / systemStats.assignments.total) * 100)
              : 0)
        },
        points: {
          total_earned: systemStats.points?.total_in_system || weeklyReport.total_points_earned || 0,
          total_redeemed: systemStats.points?.total_redeemed || 0,
          average_per_user: systemStats.points?.avg_per_user || 0,
          top_earner: weeklyReport.top_earner || { name: 'N/A', points: 0 }
        },
        activity: {
          logins_today: systemStats.activity?.logins_today || 0,
          logins_week: systemStats.activity?.logins_week || 0,
          avg_session_duration: systemStats.activity?.avg_session || '0m',
          peak_hour: systemStats.activity?.peak_hour || 'N/A'
        },
        trends: {
          daily_completions: weeklyReport.daily_stats || [],
          weekly_points: [],
          user_growth: []
        }
      }

      setStats(stats)
    } catch (error) {
      console.error('Failed to load statistics:', error)
      // Set default values if API fails
      setStats({
        users: { total: 0, active_today: 0, active_week: 0, active_month: 0, new_this_month: 0 },
        chores: { total_assigned: 0, completed: 0, pending: 0, overdue: 0, completion_rate: 0 },
        points: { total_earned: 0, total_redeemed: 0, average_per_user: 0, top_earner: { name: 'N/A', points: 0 } },
        activity: { logins_today: 0, logins_week: 0, avg_session_duration: '0m', peak_hour: 'N/A' },
        trends: { daily_completions: [], weekly_points: [], user_growth: [] }
      })
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleExport = () => {
    const csvData = [
      ['Usage Statistics Report', format(new Date(), 'yyyy-MM-dd')],
      [],
      ['User Statistics'],
      ['Total Users', stats?.users.total],
      ['Active Today', stats?.users.active_today],
      ['Active This Week', stats?.users.active_week],
      ['Active This Month', stats?.users.active_month],
      ['New This Month', stats?.users.new_this_month],
      [],
      ['Chore Statistics'],
      ['Total Assigned', stats?.chores.total_assigned],
      ['Completed', stats?.chores.completed],
      ['Pending', stats?.chores.pending],
      ['Overdue', stats?.chores.overdue],
      ['Completion Rate', `${stats?.chores.completion_rate}%`],
      [],
      ['Points Statistics'],
      ['Total Earned', stats?.points.total_earned],
      ['Total Redeemed', stats?.points.total_redeemed],
      ['Average Per User', stats?.points.average_per_user],
      ['Top Earner', `${stats?.points.top_earner.name} (${stats?.points.top_earner.points} points)`]
    ]

    const csv = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `usage-stats-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('Statistics exported successfully')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Usage Statistics</h2>
          <p className="text-gray-600">System usage metrics and analytics</p>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
          <button
            onClick={loadStats}
            disabled={refreshing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Users</p>
              <p className="text-3xl font-bold">{stats?.users.total || 0}</p>
              <p className="text-sm text-green-600">
                +{stats?.users.new_this_month || 0} this month
              </p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Completion Rate</p>
              <p className="text-3xl font-bold">{stats?.chores.completion_rate || 0}%</p>
              <p className="text-sm text-gray-600">
                {stats?.chores.completed || 0} of {stats?.chores.total_assigned || 0}
              </p>
            </div>
            <Activity className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Points Earned</p>
              <p className="text-3xl font-bold">{stats?.points.total_earned?.toLocaleString() || 0}</p>
              <p className="text-sm text-gray-600">
                Avg: {stats?.points.average_per_user || 0}/user
              </p>
            </div>
            <Award className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Today</p>
              <p className="text-3xl font-bold">{stats?.users.active_today || 0}</p>
              <p className="text-sm text-gray-600">
                {stats?.activity.logins_today || 0} logins
              </p>
            </div>
            <Clock className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">User Activity</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active This Week</span>
              <span className="font-semibold">{stats?.users.active_week || 0} users</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active This Month</span>
              <span className="font-semibold">{stats?.users.active_month || 0} users</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Avg Session Duration</span>
              <span className="font-semibold">{stats?.activity.avg_session_duration || '0m'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Peak Activity Hour</span>
              <span className="font-semibold">{stats?.activity.peak_hour || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Weekly Logins</span>
              <span className="font-semibold">{stats?.activity.logins_week || 0}</span>
            </div>
          </div>
        </div>

        {/* Chore Performance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Chore Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Assigned</span>
              <span className="font-semibold">{stats?.chores.total_assigned || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Completed</span>
              <span className="font-semibold text-green-600">{stats?.chores.completed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending</span>
              <span className="font-semibold text-yellow-600">{stats?.chores.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Overdue</span>
              <span className="font-semibold text-red-600">{stats?.chores.overdue || 0}</span>
            </div>
            <div className="pt-3 border-t">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completion Rate</span>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${stats?.chores.completion_rate || 0}%` }}
                    />
                  </div>
                  <span className="font-semibold">{stats?.chores.completion_rate || 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Points Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Points Distribution</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Points Earned</span>
              <span className="font-semibold">{stats?.points.total_earned?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Points Redeemed</span>
              <span className="font-semibold">{stats?.points.total_redeemed?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Per User</span>
              <span className="font-semibold">{stats?.points.average_per_user || 0}</span>
            </div>
            <div className="pt-3 border-t">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Top Earner</span>
                <span className="font-semibold">
                  {stats?.points.top_earner.name} ({stats?.points.top_earner.points} pts)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Trends */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Daily Completions</h3>
          {stats?.trends.daily_completions && stats.trends.daily_completions.length > 0 ? (
            <div className="space-y-2">
              {stats.trends.daily_completions.slice(-7).map((day, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {format(new Date(day.date + 'T12:00:00'), 'MMM d')}
                  </span>
                  <div className="flex items-center flex-1 mx-4">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.min((day.count / 20) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold w-8 text-right">{day.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No trend data available</p>
          )}
        </div>
      </div>
    </div>
  )
}